import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

function ThermometerMeter({
  value,
  min,
  max,
  size = 140,
  optimalMin,
  optimalMax,
  color,
}) {
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);

  // Scale factor for fonts (based on default size of 140)
  const scale = size / 140;

  const height = size;
  const width = Math.max(20, size * 0.4);
  const bulbRadius = Math.max(4, width * 0.4);
  const tubeWidth = Math.max(5, width * 0.35);
  const tubeHeight = Math.max(10, height - bulbRadius * 2 - 10);
  const fillHeight = Math.max(0, tubeHeight * normalizedValue);

  // Determine color based on value relative to optimal range, or use custom color
  const getColor = () => {
    if (color) return color;
    if (value < optimalMin) return '#3b82f6'; // cold - blue
    if (value > optimalMax) return '#ef4444'; // hot - red
    return '#22c55e'; // optimal - green
  };

  const mercuryColor = getColor();

  // Calculate optimal range markers
  const optimalMinY = tubeHeight * (1 - (optimalMin - min) / (max - min));
  const optimalMaxY = tubeHeight * (1 - (optimalMax - min) / (max - min));

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: size,
      height: height + 20,
    }}>
      <svg width={width + 40} height={height + 20} viewBox={`0 0 ${width + 40} ${height + 20}`}>
        <defs>
          <linearGradient id="tubeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.3)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.1)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.1)" />
          </linearGradient>
          <linearGradient id="mercuryGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={mercuryColor} stopOpacity={0.8} />
            <stop offset="50%" stopColor={mercuryColor} />
            <stop offset="100%" stopColor={mercuryColor} stopOpacity={0.8} />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Tube background */}
        <rect
          x={(width - tubeWidth) / 2 + 20}
          y={10}
          width={tubeWidth}
          height={tubeHeight}
          rx={tubeWidth / 2}
          fill="#1f2937"
          stroke="#374151"
          strokeWidth={2}
        />

        {/* Optimal range indicator */}
        <rect
          x={(width - tubeWidth) / 2 + 20 - 8}
          y={optimalMaxY + 10}
          width={4}
          height={Math.max(1, optimalMinY - optimalMaxY)}
          fill="#22c55e"
          opacity={0.5}
          rx={2}
        />

        {/* Mercury fill */}
        <rect
          x={(width - tubeWidth * 0.6) / 2 + 20}
          y={10 + tubeHeight - fillHeight}
          width={tubeWidth * 0.6}
          height={fillHeight}
          fill="url(#mercuryGradient)"
          rx={tubeWidth * 0.3}
          filter="url(#glow)"
        />

        {/* Bulb */}
        <circle
          cx={width / 2 + 20}
          cy={height - bulbRadius - 5}
          r={bulbRadius}
          fill="#1f2937"
          stroke="#374151"
          strokeWidth={2}
        />

        {/* Bulb mercury */}
        <circle
          cx={width / 2 + 20}
          cy={height - bulbRadius - 5}
          r={bulbRadius * 0.75}
          fill={mercuryColor}
          filter="url(#glow)"
        />

        {/* Glass reflection */}
        <rect
          x={(width - tubeWidth) / 2 + 20}
          y={10}
          width={tubeWidth}
          height={tubeHeight}
          rx={tubeWidth / 2}
          fill="url(#tubeGradient)"
          opacity={0.3}
        />

        {/* Tick marks */}
        {[0, 0.25, 0.5, 0.75, 1].map((tick, i) => (
          <g key={i}>
            <line
              x1={(width + tubeWidth) / 2 + 22}
              y1={10 + tubeHeight * (1 - tick)}
              x2={(width + tubeWidth) / 2 + 28}
              y2={10 + tubeHeight * (1 - tick)}
              stroke="#6b7280"
              strokeWidth={1.5}
            />
            <text
              x={(width + tubeWidth) / 2 + 32}
              y={10 + tubeHeight * (1 - tick) + 4}
              fill="#9ca3af"
              fontSize={Math.max(7, 9 * scale)}
            >
              {Math.round(min + (max - min) * tick)}°
            </text>
          </g>
        ))}
      </svg>
    </Box>
  );
}

ThermometerMeter.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  size: PropTypes.number,
  optimalMin: PropTypes.number,
  optimalMax: PropTypes.number,
  color: PropTypes.string,
};

export default ThermometerMeter;
